'use client';
import classNames from 'classnames/bind';
import React, { forwardRef, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useIsomorphicLayoutEffect } from '@financial/common-hooks';
import AnchorButton from '../common/AnchorButton.mjs';
import COLOR from '../../foundation/color.mjs';
import { mergeRefs } from '../../utils/ref.mjs';
import styles from './LineTab.module.scss.mjs';

const cx = classNames.bind(styles);
const LineTabItem = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    icon,
    tabName,
    active,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement("li", {
    className: cx('item'),
    ref: ref
  }, /*#__PURE__*/React.createElement(AnchorButton, {
    className: cx('link', {
      'is-selected': active
    }),
    ...rest
  }, icon && /*#__PURE__*/React.createElement("span", {
    className: cx('icon')
  }, icon), tabName && /*#__PURE__*/React.createElement("span", null, tabName), active && /*#__PURE__*/React.createElement("span", {
    className: "blind"
  }, "\uD604\uC7AC \uC704\uCE58")));
});
LineTabItem.displayName = 'LineTabItem';
const LineTab = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    bgColor = COLOR.white.name,
    typeLayout,
    type,
    maxWidthType = 'full',
    items,
    selectedKey,
    selectedIndex
  } = _ref2;
  const $tab = useRef(null);
  const $items = useRef([]);
  const [scrollable, setScrollable] = useState(false);
  const adjustScrollable = () => {
    var _$tab$current, _$tab$current2;
    const tabClientWidth = ((_$tab$current = $tab.current) === null || _$tab$current === void 0 ? void 0 : _$tab$current.clientWidth) ?? 0;
    const tabScrollWidth = ((_$tab$current2 = $tab.current) === null || _$tab$current2 === void 0 ? void 0 : _$tab$current2.scrollWidth) ?? 0;
    const isTabOverflowed = tabClientWidth < tabScrollWidth;
    setScrollable(isTabOverflowed);
  };
  useIsomorphicLayoutEffect(() => {
    if (typeLayout !== 'flexible') {
      return;
    }
    const tabElement = $tab.current;
    const resizeObserver = new ResizeObserver(adjustScrollable);
    tabElement && resizeObserver.observe(tabElement);
    return () => {
      tabElement && resizeObserver.unobserve(tabElement);
    };
  }, [typeLayout]);
  useIsomorphicLayoutEffect(() => {
    if (!scrollable) {
      return;
    }
    const targetIdx = selectedIndex ?? items.findIndex(item => selectedKey === item.key);
    if (targetIdx !== -1) {
      const $selectedTab = $items.current[targetIdx];
      $selectedTab && scrollIntoView($selectedTab, {
        inline: 'center',
        boundary: $tab.current
      });
    }
    // 선택된 키 또는 인덱스 값이 변경될 때만 스크롤 동작
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, selectedIndex, scrollable]);
  return /*#__PURE__*/React.createElement("div", {
    className: cx('article', `bg-${bgColor}`, {
      'x-scroll': scrollable
    }),
    ref: ref
  }, /*#__PURE__*/React.createElement("ul", {
    className: cx('list', type, typeLayout, maxWidthType !== 'full' ? {
      [`max-width-${maxWidthType}`]: maxWidthType
    } : undefined),
    ref: $tab
  }, items.map((_ref3, index) => {
    let {
      ref: $item,
      ...item
    } = _ref3;
    const key = item.key ?? index;
    return /*#__PURE__*/React.createElement(LineTabItem, {
      ...item,
      key: key,
      active: selectedIndex === index || selectedKey === key,
      ref: mergeRefs([$item, node => $items.current[index] = node])
    });
  })));
});
LineTab.displayName = 'LineTab';

export { LineTab as default };
